import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Search, RefreshCw, Download } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout/DasboardLayout"; // Ensure proper path
import { API_URL } from "../../components/config";
import BalanceCards from "./BalanceCards";
const GroupedTransactionTable = () => {
  const [transactions, setTransactions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("client_profile");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const groupOptions = [
    { value: "client_profile", label: "Client Profile" },
    { value: "vendor", label: "Vendor" },
    { value: "payment_gateway", label: "Payment Gateway" },
    { value: "main_wallet", label: "Main Wallet" },
    { value: "revenue_wallet", label: "Revenue Wallet" },
    { value: "escrow", label: "Escrow" },
    { value: "promo_wallet", label: "Promo Wallet" },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/topUp/`);
      const result = await response.json();
      setTransactions(result.results);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const handleExport = () => {
    const csvData = transactions.map((transaction) => ({
      TopUpID: transaction.top_up_id,
      Currency: transaction.currency,
      ExchangeRate: transaction.exchange_rate,
      Date: transaction.date,
      Description: transaction.description,
      Amount: transaction.amount,
      TripID: transaction.trip_id,
      TransactionCode: transaction.trxn_code,
      Folio: transaction.folio,
      ...transaction,
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "transactions.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        Object.values(transaction).some(
          (value) =>
            value && value.toString().toLowerCase().includes(searchLower)
        );

      let matchesGroup = true;
      switch (selectedGroup) {
        case "client_profile":
          matchesGroup =
            (transaction.user_wallet_debit !== null &&
              transaction.user_wallet_debit !== 0) ||
            (transaction.user_wallet_credit !== 0 &&
              transaction.user_wallet_credit !== null);
          break;
        case "vendor":
          matchesGroup =
            (transaction.vendor_wallet_debit !== null &&
              transaction.vendor_wallet_debit !== 0) ||
            (transaction.vendor_wallet_credit !== null &&
              transaction.vendor_wallet_credit !== 0);
          break;
        case "payment_gateway":
          matchesGroup =
            (transaction.payment_gateway_charges_debit !== null &&
              transaction.payment_gateway_charges_debit !== 0) ||
            (transaction.payment_gateway_charges_credit !== null &&
              transaction.payment_gateway_charges_credit !== 0);
          break;
        case "main_wallet":
          matchesGroup =
            (transaction.main_wallet_debit !== null &&
              transaction.main_wallet_debit !== 0) ||
            (transaction.main_wallet_credit !== null &&
              transaction.main_wallet_credit !== 0);
          break;
        case "revenue_wallet":
          matchesGroup =
            (transaction.revenue_wallet_debit !== null &&
              transaction.revenue_wallet_debit !== 0) ||
            (transaction.revenue_wallet_credit !== null &&
              transaction.revenue_wallet_credit !== 0);
          break;
        case "escrow":
          matchesGroup =
            (transaction.escrow_debit !== null &&
              transaction.escrow_debit !== 0) ||
            (transaction.escrow_credit !== null &&
              transaction.escrow_credit !== 0);
          break;
        case "promo_wallet":
          matchesGroup =
            (transaction.promo_wallet_debit !== null &&
              transaction.promo_wallet_debit !== 0) ||
            (transaction.promo_wallet_credit !== null &&
              transaction.promo_wallet_credit !== 0) ||
            (transaction.promo_wallet_balance !== null &&
              transaction.promo_wallet_balance !== 0) ||
            (transaction.promo_wallet_total_balance !== null &&
              transaction.promo_wallet_total_balance !== 0);
          break;
        default:
          matchesGroup = true;
      }

      return matchesSearch && matchesGroup;
    });
  }, [transactions, selectedGroup, searchTerm]);

  const renderGroupSpecificColumns = (transaction) => {
    const columns = {
      client_profile: [
        { key: "user_wallet_debit", label: "User Wallet Debit" },
        { key: "user_wallet_credit", label: "User Wallet Credit" },
        { key: "user_wallet_balance", label: "User Wallet Balance" },
        {
          key: "user_wallet_total_balance",
          label: "User Wallet Total Balance",
        },
      ],
      vendor: [
        { key: "vendor_wallet_debit", label: "Vendor Wallet Debit" },
        { key: "vendor_wallet_credit", label: "Vendor Wallet Credit" },
        { key: "vendor_wallet_balance", label: "Vendor Wallet Balance" },
        {
          key: "vendor_wallet_total_balance",
          label: "Vendor Wallet Total Balance",
        },
      ],
      payment_gateway: [
        {
          key: "payment_gateway_charges_debit",
          label: "Payment Gateway Charges Debit",
        },
        {
          key: "payment_gateway_charges_credit",
          label: "Payment Gateway Charges Credit",
        },
        {
          key: "payment_gateway_charges_balance",
          label: "Payment Gateway Charges Balance",
        },
        {
          key: "payment_gateway_charges_total_balance",
          label: "Payment Gateway Charges Total Balance",
        },
      ],
      main_wallet: [
        { key: "main_wallet_debit", label: "Main Wallet Debit" },
        { key: "main_wallet_credit", label: "Main Wallet Credit" },
        { key: "main_wallet_balance", label: "Main Wallet Balance" },
        {
          key: "main_wallet_total_balance",
          label: "Main Wallet Total Balance",
        },
      ],
      revenue_wallet: [
        { key: "revenue_wallet_debit", label: "Revenue Wallet Debit" },
        { key: "revenue_wallet_credit", label: "Revenue Wallet Credit" },
        { key: "revenue_wallet_balance", label: "Revenue Wallet Balance" },
        {
          key: "revenue_wallet_total_balance",
          label: "Revenue Wallet Total Balance",
        },
      ],
      escrow: [
        { key: "escrow_debit", label: "Escrow Debit" },
        { key: "escrow_credit", label: "Escrow Credit" },
        { key: "escrow_balance", label: "Escrow Balance" },
        { key: "escrow_total_balance", label: "Escrow Total Balance" },
      ],
      promo_wallet: [
        { key: "promo_wallet_debit", label: "Promo Wallet Debit" },
        { key: "promo_wallet_credit", label: "Promo Wallet Credit" },
        { key: "promo_wallet_balance", label: "Promo Wallet Balance" },
        { key: "promo_wallet_total_balance", label: "Promo Wallet Total Balance" },
      ],
    };

    const groupColumns = columns[selectedGroup] || [];
    return groupColumns.map(({ key, label }) => (
      <td key={key} className="border-t py-3 px-4 text-sm">
        {transaction[key] || "0"}
      </td>
    ));
  };

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-8 bg-gradient-to-b from-gray-50 to-white rounded-xl shadow-lg max-w-[98%] mx-auto"
      >
        <BalanceCards />

        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6">
          <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
            <div className="relative w-full md:w-64">
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full appearance-none bg-white border-2 border-gray-200 rounded-lg py-2.5 px-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-200 hover:border-gray-300"
              >
                {groupOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>

            <div className="relative w-full md:w-80">
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all duration-200 hover:border-gray-300 text-gray-700 placeholder-gray-400"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <button
              onClick={handleRefresh}
              className="flex-1 md:flex-none px-6 py-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all duration-200 font-medium flex items-center justify-center gap-2 hover:shadow-md active:scale-95"
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="flex-1 md:flex-none px-6 py-2.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-all duration-200 font-medium flex items-center justify-center gap-2 hover:shadow-md active:scale-95"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Top Up ID
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Exchange Rate
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Trip ID
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Transaction Code
                  </th>
                  <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Folio
                  </th>
                  {selectedGroup === "client_profile" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        User Wallet Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        User Wallet Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        User Wallet Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        User Wallet Total Balance
                      </th>
                    </>
                  )}
                  {selectedGroup === "vendor" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Vendor Wallet Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Vendor Wallet Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Vendor Wallet Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Vendor Wallet Total Balance
                      </th>
                    </>
                  )}
                  {selectedGroup === "payment_gateway" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Payment Gateway Charges Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Payment Gateway Charges Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Payment Gateway Charges Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Payment Gateway Charges Total Balance
                      </th>
                    </>
                  )}
                  {selectedGroup === "main_wallet" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Main Wallet Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Main Wallet Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Main Wallet Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Main Wallet Total Balance
                      </th>
                    </>
                  )}
                  {selectedGroup === "revenue_wallet" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Revenue Wallet Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Revenue Wallet Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Revenue Wallet Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Revenue Wallet Total Balance
                      </th>
                    </>
                  )}
                  {selectedGroup === "escrow" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Escrow Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Escrow Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Escrow Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Escrow Total Balance
                      </th>
                    </>
                  )}
                  {selectedGroup === "promo_wallet" && (
                    <>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Promo Wallet Debit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Promo Wallet Credit
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Promo Wallet Balance
                      </th>
                      <th className="py-3.5 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Promo Wallet Total Balance
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <motion.tr
                    key={transaction.top_up_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.top_up_id}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.currency || "N/A"}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.exchange_rate}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.date}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.description || "N/A"}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.amount}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.trip_id || "N/A"}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.trxn_code || "N/A"}
                    </td>
                    <td className="whitespace-nowrap py-4 px-4 text-sm text-gray-700">
                      {transaction.folio || "N/A"}
                    </td>
                    {renderGroupSpecificColumns(transaction)}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer Section */}
        <div className="mt-6 flex justify-between items-center text-sm text-gray-500">
          <span>
            Showing {filteredTransactions.length} of {transactions.length}{" "}
            transactions
          </span>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="px-4 py-2 bg-gray-50 rounded-lg"
          >
            Total entries: {transactions.length}
          </motion.div>
        </div>
      </motion.div>
    </DashboardLayout>
  );
};

export default GroupedTransactionTable;
